import React, {useCallback} from "react";
import {Box, IconButton, Link, Stack, TableCell, Tooltip, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {DataTable} from "../../components/data-table/DataTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {Office} from "../../api/Model";
import {idFn, noop} from "../../util/FunctionUtil";
import {officeGoogleMapLink} from "../../components/office-chip/OfficeChip";
import {OfficeIcon} from "../../components/office-icon/OfficeIcon";
import {Tag} from "../../components/tag/Tag";
import {SiteChip} from "../../components/site-chip/SiteChip";

function ActionColumn({office}: { office: Office }) {
    const {onEdit} = React.useContext(OfficeTableContext);
    const handleEdit = useCallback(() => onEdit(office), [onEdit, office]);
    return (
        <TableCell align="right">
            <Tooltip title="Edit">
                <IconButton color="primary" onClick={handleEdit}>
                    <EditIcon/>
                </IconButton>
            </Tooltip>
        </TableCell>
    );
}

function IntegrationsColumn({office}: { office: Office }) {
    return (
        <TableCell>
            <Stack direction="row" gap={1} flexWrap="wrap">
                {office.checkInsIntegrationEnabled && (
                    <Tooltip title="Integration with JetBrains Check-ins app is active">
                        <Box>
                            <Tag color="primary" typography="caption">
                                Check-Ins
                            </Tag>
                        </Box>
                    </Tooltip>
                )}
                {office.releezmeIntegrationEnabled && (
                    <Tooltip title="Integration with VECOS Releezme is active">
                        <Box>
                            <Tag color="secondary" typography="caption">
                                Releezme
                            </Tag>
                        </Box>
                    </Tooltip>
                )}
            </Stack>
        </TableCell>
    );
}

const columns: TableColumnData<Office>[] = [
    {
        key: 'icon',
        header: '',
        cell: office => <TableCell><OfficeIcon office={office} colored fontSize="large"/></TableCell>
    },
    {
        key: 'name',
        header: 'Name',
        cell: office => (
            <TableCell>
                <Stack direction="row" gap={1}>
                    <Typography variant="body1">{office.name}</Typography>
                    {office.disabled && <Tag color="disabled" typography="caption">inactive</Tag>}
                </Stack>
            </TableCell>
        )
    },
    {
        key: 'address',
        header: 'Address',
        cell: office => {
            const link = officeGoogleMapLink(office);
            const address = office.addressLineOne || 'No address';
            return (
                <TableCell>{link ? (
                    <Stack direction="row" gap={1}>
                        <LocationOnIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                        <Link href={link} target="_blank" rel="noopener noreferrer">{address}</Link>
                    </Stack>
                ) : (
                    address
                )}</TableCell>
            );
        }
    },
    {
        key: 'timezone',
        header: 'Timezone',
        cell: office => <TableCell>{office.timezone}</TableCell>
    },
    {
        key: 'hibobResidence',
        header: 'HiBob',
        width: '10em',
        cell: office => (
            <TableCell>
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {office.hibobResidence.map(residence => residence.hibobOffice ? (
                        <SiteChip
                            key={`${residence.workSite?.id}-${residence.hibobOffice.id}`}
                            siteId={residence.workSite?.id ?? residence.hibobOffice.workSiteId}
                            hibobOfficeId={residence.hibobOffice.id}
                        />
                    ) : (
                        <SiteChip
                            key={residence.workSite?.id ?? 'null'}
                            siteId={residence.workSite?.id}
                        />
                    ))}
                </Stack>
            </TableCell>
        )
    },
    {
        key: 'integrations',
        header: 'Integrations',
        width: '10em',
        cell: office => <IntegrationsColumn office={office}/>
    },
    {
        key: 'actions',
        header: 'Actions',
        width: '8em',
        align: 'right',
        cell: office => <ActionColumn office={office}/>
    },
];

const OfficeTableContext = React.createContext<{ onEdit: (office: Office) => void }>({
    onEdit: noop,
});

export function OfficeTable({rows, onEdit}: {
    rows: Office[],
    onEdit: (office: Office) => void
}) {
    return (
        <OfficeTableContext.Provider value={{onEdit}}>
            <DataTable
                id={idFn}
                columns={columns}
                rows={rows}
            />
        </OfficeTableContext.Provider>
    );
}
